import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, ArrowLeft, Users, Layers, Shield, Calendar, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClubSignupPage = () => {
  const [formData, setFormData] = useState({
    club_name: '',
    contact_name: '',
    email: '',
    phone: '',
    num_teams: '',
    sport: 'soccer',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        business_name: formData.club_name,
        sport: 'soccer',
        message: `[Soccer Club Registration Request]\nNumber of Teams: ${formData.num_teams}\nAge Groups: ${formData.age_groups || 'Not specified'}\n\n${formData.message}`,
        request_type: 'club_registration'
      });
      setSubmitted(true);
      toast.success('Registration request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  const features = [
    { icon: Layers, title: 'Multi-Team Management', desc: 'Organize teams by age group or category' },
    { icon: Users, title: 'Coach Assignment', desc: 'Assign coaches and managers to teams' },
    { icon: Calendar, title: 'Centralized Sessions', desc: 'Manage training schedules across teams' },
    { icon: Shield, title: 'External Billing', desc: 'Handle payments outside the platform' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-emerald-400" size={40} />
          </div>
          <h1 className="font-heading text-3xl uppercase mb-4 text-emerald-400">Request Received!</h1>
          <p className="text-muted-foreground mb-8">
            Thank you for your interest in Meybra for your club. Our team will contact you within 48 hours to discuss your needs and get you set up.
          </p>
          <Link to="/">
            <Button variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span className="text-sm uppercase tracking-widest">Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
                <Building2 className="text-emerald-400" size={16} />
                <span className="text-emerald-400 text-sm font-semibold">Club Registration</span>
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-6">
                Register Your<br />
                <span className="text-emerald-400">Soccer Club or Academy</span>
              </h1>
              
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Meybra helps soccer clubs manage multiple teams, assign coaches, and centralize player development — 
                all while keeping billing handled externally.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {features.map((feature, idx) => (
                  <div key={idx} className="bg-card/50 border border-border/40 rounded-sm p-4">
                    <div className="w-10 h-10 bg-emerald-500/10 rounded-sm flex items-center justify-center mb-3">
                      <feature.icon className="text-emerald-400" size={20} />
                    </div>
                    <h3 className="font-semibold mb-1">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                ))}
              </div>
              
              <div className="bg-primary/10 border border-primary/30 rounded-sm p-4">
                <p className="text-sm text-primary font-semibold mb-1">Founding Beta Access</p>
                <p className="text-sm text-muted-foreground">
                  We're onboarding founding clubs one at a time to ensure the best experience. 
                  Submit your request and our team will reach out.
                </p>
              </div>
            </motion.div>
            
            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="bg-card border border-border/40 rounded-sm p-6 md:p-8 space-y-5" data-testid="club-signup-form">
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Club / Academy Name *</label>
                  <Input
                    value={formData.club_name}
                    onChange={(e) => setFormData({...formData, club_name: e.target.value})}
                    required
                    className="bg-transparent border-border focus:border-emerald-500"
                    placeholder="Your club name"
                    data-testid="club-name-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Contact Name *</label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    required
                    className="bg-transparent border-border focus:border-emerald-500"
                    placeholder="Your full name"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Email *</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="bg-transparent border-border focus:border-emerald-500"
                      placeholder="email@club.com"
                      data-testid="club-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Phone</label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-transparent border-border focus:border-emerald-500"
                      placeholder="(555) 123-4567"
                      data-testid="club-phone-input"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Number of Soccer Teams</label>
                    <Input
                      type="number"
                      value={formData.num_teams}
                      onChange={(e) => setFormData({...formData, num_teams: e.target.value})}
                      className="bg-transparent border-border focus:border-emerald-500"
                      placeholder="e.g., 5"
                      data-testid="num-teams-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Age Groups</label>
                    <Input
                      type="text"
                      value={formData.age_groups || ''}
                      onChange={(e) => setFormData({...formData, age_groups: e.target.value})}
                      className="bg-transparent border-border focus:border-emerald-500"
                      placeholder="e.g., U12, U14, U16"
                      data-testid="age-groups-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Additional Information</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="bg-transparent border-border focus:border-emerald-500 min-h-[100px]"
                    placeholder="Tell us about your soccer club, teams, and what you're looking for..."
                    data-testid="club-message-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-500 text-black hover:bg-emerald-400 uppercase tracking-wider py-6 font-bold"
                  data-testid="club-submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Registration Request'}
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  By submitting, you agree to be contacted by our team regarding club onboarding.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubSignupPage;
